import * as styles from './header.module.css';
import React, { useState, useEffect, useRef } from 'react';
import {
  Header,
  HeaderName,
  SkipToContent,
  HeaderGlobalBar,
  HeaderGlobalAction,
  Theme,
  Modal,
  ComboBox
} from '@carbon/react';
import { User } from '@carbon/icons-react';
import { ExpressiveCard } from '@carbon/ibm-products';
import { useNavigate } from 'react-router';
import { deleteSession, getSession } from '../../lib/auth';
import AuthService from '../../services/auth.service';
import { EntitlementData, Tenant } from '../../services/api.models';
import { logger } from '../../lib/logger';
import { eventBus } from '../utils/eventBus';
import type { UseComboboxActions } from 'downshift';
import { localStorageActions, storageKeys } from '../utils/localStorageActions';
interface PageHeaderProps {
  showAvatar?: boolean;
}
interface TenantSelectedItem {
  id: string;
  text: string;
}
const authService = new AuthService();

export default function PageHeader({ showAvatar = true }: PageHeaderProps) {
  const [showProfile, setShowProfile] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    displayName?: string;
    userName?: string;
    customerId?: string;
    sub?: string;
    tenantId?: string;
  } | null>(null);
  const [tenantList, setTenantList] = useState<TenantSelectedItem[] | null>([]);
  const [selectedTenant, setSelectedTenant] =
    useState<TenantSelectedItem | null>(null);
  const [userData, setUserData] = useState<EntitlementData | null>(null);
  const downshiftActions =
    useRef<UseComboboxActions<TenantSelectedItem>>(undefined);
  useEffect(() => {
    const loadUserInfo = async () => {
      const session = getSession();
      if (session.isValid()) {
        const info = session.getUserInfo();
        setUserInfo(info);
        //To get ALL ess entitlements.
        authService.getEntitlements(`${info?.sub}`, 'ALL').then((data) => {
          const tenants = prepareTenants(data?.tenants ?? []);
          const defaultSelectedTenant = tenants.filter(
            (t) => t.id === info?.tenantId
          )[0];
          setTenantList(tenants);
          setSelectedTenant(defaultSelectedTenant || null);
          downshiftActions.current?.selectItem(defaultSelectedTenant);
        });

        //To get only tenant
        const userId = info?.sub;
        const tenantId = info?.tenantId;
        if (userId && tenantId) {
          try {
            const tenantData = await authService.getEntitlements(
              userId,
              tenantId
            );
            setUserData(tenantData);
          } catch (error) {
            logger.error('Error fetching entitlements:', error);
          }
        }
      }
    };
    loadUserInfo();
  }, []);
  const navigate = useNavigate();

  const handleLogOut = () => {
    fetch('/api/auth/logout').then(() => {
      deleteSession();
      navigate('/logout');
    });
  };

  const prepareTenants = (tenants: Tenant[]) => {
    return tenants.map((tenant) => {
      return {
        id: tenant.id,
        text: `${tenant.id} - ${tenant?.customer?.name} (${tenant?.customer?.key})`
      };
    });
  };

  const renderTenantDetails = () => {
    if (userData?.tenants && userData.tenants.length > 0) {
      const tenant = userData.tenants[0];
      const [pid, serial] = tenant.id.split(':');

      return (
        <div className={styles.cardContent}>
          <p className={styles.customerName}>{tenant.customer.name}</p>
          <p className={styles.tenantPID}>PID: {pid}</p>
          <p className={styles.tenantSerial}>Serial: {serial}</p>
          {tenant.entitlements.map((entitlement) => (
            <div key={entitlement.id}>
              <p className={styles.entitlementName}>
                {entitlement.name} (expires {entitlement.expiration})
              </p>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div data-testid="ibm-platform-name" className="App">
        <Theme theme="g100">
          <Header aria-label="IBM Platform Name" className={styles.topHeader}>
            <SkipToContent />
            <div className={styles.headerNameWrapper}>
              <HeaderName
                className={styles.headerName}
                prefix="IBM"
                onClick={() => {
                  navigate('/');
                }}
              >
                Transformation Suite for SAP Applications
              </HeaderName>
            </div>
            {showAvatar && (
              <>
                <div className={styles.entitlementComboBox}>
                  <ComboBox
                    id="entitlement-combobox"
                    placeholder="Entitlement name here"
                    selectedItem={selectedTenant}
                    items={tenantList ?? []}
                    itemToString={(item) => (item ? item.text : '')}
                    onChange={({ selectedItem }) => {
                      if (selectedItem === null) return;
                      const safeValue = selectedItem ?? null;
                      // Save to localStorage
                      const previous = localStorageActions.get(
                        storageKeys.LAST_LOGIN_TENANT_ID
                      );
                      if (previous !== safeValue?.id) {
                        localStorageActions.set(
                          storageKeys.LAST_LOGIN_TENANT_ID,
                          safeValue?.id ?? ''
                        );
                      }
                      setSelectedTenant(safeValue);
                      const picked = {
                        selectedItem: safeValue,
                        currentTenant: userInfo?.tenantId
                      };
                      eventBus.publish('onUserInput', picked);
                    }}
                    downshiftActions={downshiftActions}
                    downshiftProps={{
                      onStateChange: (changes) => {
                        if (changes.selectedItem === undefined) {
                          return;
                        }
                      }
                    }}
                  />
                </div>

                <HeaderGlobalBar className={styles.userAvatar}>
                  <div className={styles.avatarContainer}>
                    <div id="data-profile-btn">
                      <HeaderGlobalAction
                        aria-label="Profile"
                        onClick={() => {
                          setShowProfile((prev) => !prev);
                        }}
                      >
                        <User size={25} />
                      </HeaderGlobalAction>
                    </div>
                    {showProfile && (
                      <div className={styles.cardWrapper} id="data-logout-btn">
                        <ExpressiveCard
                          className={styles.ExpressiveCard}
                          title={userInfo?.displayName}
                          media={
                            <User className={styles.ProfileIcon} size={40} />
                          }
                          primaryButtonText="Log Out"
                          mediaPosition="left"
                          onPrimaryButtonClick={() => setShowModal(true)}
                        >
                          <div className={styles.cardContent}>
                            <p className={styles.email}>{userInfo?.userName}</p>
                            {renderTenantDetails()}
                          </div>
                        </ExpressiveCard>
                      </div>
                    )}
                  </div>
                </HeaderGlobalBar>
              </>
            )}
          </Header>

          <Modal
            id="data-confirm-logout-btn"
            open={showModal}
            onRequestClose={() => setShowModal(false)}
            modalHeading="Do you want to log out?"
            modalLabel="Confirm"
            primaryButtonText="Log out"
            secondaryButtonText="Cancel"
            onRequestSubmit={handleLogOut}
          />
        </Theme>
      </div>
    </>
  );
}
